/* =========================================================
   IDENTITY LUXE — Family Pack builder
   2026-10-22: cards keep the real price + small green "4th piece" line;
   mobile fixed bottom bar (progress, message, total, saved, checkout).
   2026-10-30: meter starts at "1 piece — no discount" (then 2 / 3 / 4+);
   "✓ Added: <product>" line in the pack; on mobile the pack sheet opens after
   every add; "+ free delivery" not repeated once already unlocked.
   ========================================================= */
(() => {
  if (customElements.get('il-family-builder')) return;

  // the old local "pack" (kept apart from the cart) is no longer used — clean it up
  try { localStorage.removeItem('il-family-pack-v1'); } catch (e) { /* ignore */ }
  const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    format = format || '{{amount}}';
    const fmt = (n, dec, th = ',', dp = '.') => {
      const fixed = (n / 100).toFixed(dec).split('.');
      return fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, th) + (fixed[1] ? dp + fixed[1] : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    }).replace(/<[^>]*>/g, '');
  }

  // "4th" / "الرابعة"
  const AR_ORD = ['', 'الأولى', 'الثانية', 'الثالثة', 'الرابعة', 'الخامسة', 'السادسة', 'السابعة', 'الثامنة', 'التاسعة', 'العاشرة'];
  function ordinal(n, lang) {
    if (lang === 'ar') return AR_ORD[n] || ('رقم ' + n);
    const m100 = n % 100; const m10 = n % 10;
    const suf = (m100 >= 11 && m100 <= 13) ? 'th' : (m10 === 1 ? 'st' : m10 === 2 ? 'nd' : m10 === 3 ? 'rd' : 'th');
    return n + suf;
  }

  class ILFamilyBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfg = JSON.parse(this.querySelector('[data-il-fb-config]').textContent);
      this.cfg = cfg;
      this.t = cfg.t;
      this.tiers = (cfg.tiers || []).filter((x) => x.qty > 0).sort((a, b) => a.qty - b.qty);
      // meter: "1 piece — no discount" first, then the tiers (2 / 3 / 4+)
      this.steps = this.tiers.length && this.tiers[0].qty > 1
        ? [{ qty: 1, amount: 0, ship: false, start: true }].concat(this.tiers)
        : this.tiers.slice();
      this.cart = cfg.cart || null;

      this.tray = this.querySelector('[data-il-tray]');
      this.dialog = this.querySelector('[data-il-size-dialog]');
      this.finder = this.dialog && this.dialog.querySelector('il-size-finder');

      this.setupTabs();
      this.querySelectorAll('[data-il-card]').forEach((card) => this.setupCard(card));
      this.setupTray();
      this.setupDialog();
      this.buildMeterSteps();
      this.render();
      this.refreshCart();   // fresh copy (the page may come from the browser cache)
    }

    /* ---------- Tabs ---------- */
    setupTabs() {
      const tabs = [...this.querySelectorAll('[data-il-tab]')];
      const groups = [...this.querySelectorAll('[data-il-group]')];
      if (groups.length < 2) { this.querySelector('.il-fb__tabs').hidden = true; return; }
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        const id = tab.dataset.ilTab;
        tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        groups.forEach((g) => { g.hidden = !(id === 'all' || g.dataset.ilGroup === id); });
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
        const shop = this.querySelector('.il-fb__shop');
        if (shop.getBoundingClientRect().top < 0) {
          const y = shop.getBoundingClientRect().top + window.scrollY - 90;
          window.ilLenis ? window.ilLenis.scrollTo(y) : window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }));
    }

    /* ---------- Product cards ---------- */
    setupCard(card) {
      const data = JSON.parse(card.querySelector('[data-il-card-json]').textContent);
      card._data = data;
      // lowest price of the product (for the "4th piece" line)
      const avail = data.variants.filter((v) => v.a);
      card._minPrice = Math.min(...(avail.length ? avail : data.variants).map((v) => Number(v.p) || 0));
      card._dealEl = card.querySelector('[data-il-card-deal]');
      const sizeIndex = parseInt(card.dataset.sizeIndex, 10);
      const optEls = [...card.querySelectorAll('[data-il-opt]')];
      const img = card.querySelector('[data-il-card-img]');
      const addBtn = card.querySelector('[data-il-add]');
      const addLabel = card.querySelector('[data-il-add-label]');

      const selection = () => optEls.map((el) => {
        const on = el.querySelector('.il-fb-chip[aria-pressed="true"]');
        return on ? on.dataset.value : null;
      });
      const findVariant = (sel) => data.variants.find((v) => v.o.every((val, i) => sel[i] === val));

      const refresh = () => {
        const sel = selection();
        // availability of every chip given the other selected options
        optEls.forEach((el, oi) => {
          el.querySelectorAll('.il-fb-chip').forEach((chip) => {
            const ok = data.variants.some((v) => v.a && v.o[oi] === chip.dataset.value &&
              v.o.every((val, j) => j === oi || sel[j] == null || sel[j] === val));
            chip.classList.toggle('is-unavailable', !ok);
          });
          const label = el.querySelector('[data-il-opt-value]');
          if (label) label.textContent = sel[oi] || '';
        });
        const complete = sel.every((v) => v != null) || data.variants.length === 1;
        const variant = data.variants.length === 1 ? data.variants[0] : (complete ? findVariant(sel) : null);
        // swap image to the selected colour
        if (img) {
          const byColour = variant || data.variants.find((v) => v.img && v.o.every((val, i) => i === sizeIndex || sel[i] == null || sel[i] === val));
          if (byColour && byColour.img && img.dataset.current !== byColour.img) {
            img.dataset.current = byColour.img;
            img.removeAttribute('srcset');
            img.src = byColour.img;
          }
        }
        card._variant = variant;
        if (!data.variants.some((v) => v.a)) {
          addLabel.textContent = this.t.soldout; addBtn.disabled = true;
        } else if (!complete) {
          addLabel.textContent = this.t.choose; addBtn.disabled = false; addBtn.classList.add('is-waiting');
        } else if (!variant || !variant.a) {
          addLabel.textContent = this.t.soldout; addBtn.disabled = true;
        } else {
          addLabel.textContent = this.t.add; addBtn.disabled = false; addBtn.classList.remove('is-waiting');
        }
      };
      card._refresh = refresh;

      optEls.forEach((el) => {
        el.addEventListener('click', (e) => {
          const chip = e.target.closest('.il-fb-chip');
          if (!chip) return;
          el.querySelectorAll('.il-fb-chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
          el.classList.remove('is-missing');
          refresh();
        });
      });

      const help = card.querySelector('[data-il-findsize]');
      if (help) help.addEventListener('click', () => this.openSizeDialog(card));

      addBtn.addEventListener('click', () => {
        const sel = selection();
        const missing = optEls.filter((el, i) => sel[i] == null);
        if (missing.length && data.variants.length > 1) {
          missing.forEach((el) => {
            el.classList.remove('is-missing'); void el.offsetWidth; el.classList.add('is-missing');
          });
          return;
        }
        const v = card._variant;
        if (!v || !v.a || addBtn.classList.contains('is-loading')) return;
        addBtn.classList.add('is-loading');
        addLabel.textContent = this.t.adding;
        this.add(v)
          .then(() => {
            this.showAdded(data.title, v);
            if (this.isMobile()) this.toggleTray(true); else this.fly(card);
            addBtn.classList.add('is-done');
            addLabel.textContent = this.t.added + ' ✓';
            // reset size so the next family member picks their own
            if (sizeIndex > -1 && optEls[sizeIndex]) {
              optEls[sizeIndex].querySelectorAll('.il-fb-chip').forEach((c) => c.setAttribute('aria-pressed', 'false'));
            }
          })
          .catch(() => { addLabel.textContent = this.t.error; })
          .finally(() => {
            addBtn.classList.remove('is-loading');
            clearTimeout(card._t);
            card._t = setTimeout(() => { addBtn.classList.remove('is-done'); refresh(); }, 1400);
          });
      });

      refresh();
    }

    /* ---------- Size dialog ---------- */
    setupDialog() {
      if (!this.dialog) return;
      this.dialog.querySelector('[data-il-dialog-close]').addEventListener('click', () => this.closeDialog());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeDialog(); });
      this.dialog.addEventListener('close', () => window.ilLenis && window.ilLenis.start());
      this.dialog.addEventListener('il:size', (e) => {
        const card = this._sizeCard;
        if (!card) return;
        const sizeEl = card.querySelector(`[data-il-opt="${card.dataset.sizeIndex}"]`);
        const chip = sizeEl && [...sizeEl.querySelectorAll('.il-fb-chip')].find((c) => c.dataset.value === e.detail.label);
        if (chip) {
          sizeEl.querySelectorAll('.il-fb-chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
          sizeEl.classList.remove('is-missing');
          card._refresh();
        }
        setTimeout(() => this.closeDialog(), 1600);
      });
    }
    openSizeDialog(card) {
      if (!this.dialog || !this.finder) return;
      this._sizeCard = card;
      const sizeEl = card.querySelector(`[data-il-opt="${card.dataset.sizeIndex}"]`);
      const sizes = [...sizeEl.querySelectorAll('.il-fb-chip')].map((c) => c.dataset.value);
      this.finder.setSizes(sizes, card.dataset.productTitle);
      if (window.ilLenis) window.ilLenis.stop();
      if (this.dialog.showModal) this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      setTimeout(() => { const i = this.finder.querySelector('[data-il-sf-h]'); i && i.focus(); }, 60);
    }
    closeDialog() {
      if (!this.dialog) return;
      if (this.dialog.close) this.dialog.close(); else this.dialog.removeAttribute('open');
      if (window.ilLenis) window.ilLenis.start();
    }

    /* ---------- Pack state = the real Shopify cart (2026-10) ---------- */
    api(url, body) {
      return fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      }).then((r) => r.json().then((json) => {
        if (!r.ok || json.status) throw new Error(json.description || json.message || this.t.error);
        return json;
      }));
    }
    setBusy(on) {
      this._busy = on;
      this.tray.classList.toggle('is-busy', on);
    }
    showError(msg) {
      const err = this.querySelector('[data-il-error]');
      err.textContent = msg || '';
      err.hidden = !msg;
    }
    updateBubble(sections) {
      const html = sections && sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (el) bubble.innerHTML = el.innerHTML;
    }
    announce() {
      document.dispatchEvent(new CustomEvent('il:cart-changed', { detail: { source: 'il-family-builder' } }));
    }
    refreshCart() {
      return this.api(this.cfg.cartJsUrl).then((cart) => { this.cart = cart; this.render(); }).catch(() => {});
    }
    add(v) {
      this.showError('');
      this.setBusy(true);
      return this.api(this.cfg.cartAddUrl, { items: [{ id: v.id, quantity: 1 }], sections: 'cart-icon-bubble' })
        .then((res) => {
          this.updateBubble(res.sections);
          this._newId = v.id;
          return this.refreshCart();
        })
        .then(() => { this.render(true); this.announce(); })
        .catch((e) => { this.showError(e.message); throw e; })
        .finally(() => this.setBusy(false));
    }
    change(key, qty) {
      if (this._busy) return;
      this.showError('');
      this.setBusy(true);
      this.api(this.cfg.cartChangeUrl, { id: key, quantity: Math.max(0, qty), sections: 'cart-icon-bubble' })
        .then((cart) => {
          this.updateBubble(cart.sections);
          this.cart = cart;
          this.render();
          this.announce();
        })
        .catch((e) => { this.showError(e.message); this.refreshCart(); })
        .finally(() => this.setBusy(false));
    }

    isGift(item) {
      return !!((item.properties && item.properties._gift) || (this.cfg.giftProductId && item.product_id === this.cfg.giftProductId));
    }
    lines() { return (this.cart && this.cart.items) || []; }
    count() { return this.lines().reduce((n, x) => n + (this.isGift(x) ? 0 : x.quantity), 0); }

    tierFor(n) {
      let cur = null; let next = null;
      this.tiers.forEach((t) => { if (n >= t.qty) cur = t; else if (!next) next = t; });
      return { cur, next };
    }

    /* ---------- Tray + mobile bar ---------- */
    isMobile() { return window.matchMedia('(max-width: 989px)').matches; }
    // "✓ Added: Hoodie — M" at the top of the pack
    showAdded(title, v) {
      const el = this.querySelector('[data-il-added]');
      if (!el || !this.t.justAdded) return;
      const variant = v && v.t && v.t !== 'Default Title' ? ' — ' + v.t : '';
      el.querySelector('[data-il-added-text]').textContent = this.t.justAdded.replace('[title]', (title || '') + variant);
      el.hidden = false;
      clearTimeout(this._addedT);
      this._addedT = setTimeout(() => { el.hidden = true; }, 6000);
    }
    toggleTray(open) {
      this.tray.classList.toggle('is-open', open);
      this.querySelectorAll('[data-il-tray-toggle][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
      document.documentElement.classList.toggle('il-fb-sheet-open', open);
    }
    setupTray() {
      this.querySelectorAll('[data-il-tray-toggle]').forEach((btn) => btn.addEventListener('click', () => {
        this.toggleTray(!this.tray.classList.contains('is-open'));
      }));
      // tap on the dark overlay closes the mobile sheet
      this.tray.addEventListener('click', (e) => {
        if (e.target === this.tray && this.tray.classList.contains('is-open')) this.toggleTray(false);
      });
      this.querySelector('[data-il-list]').addEventListener('click', (e) => {
        const btn = e.target.closest('[data-il-qty]');
        if (!btn) return;
        const line = this.lines().find((x) => x.key === btn.dataset.key);
        if (line) this.change(line.key, line.quantity + Number(btn.dataset.ilQty));
      });
      // pack panel checkout + mobile bar checkout
      this.querySelectorAll('[data-il-checkout]').forEach((btn) => btn.addEventListener('click', () => {
        if (!this.count()) return;
        window.location.href = this.cfg.goToCart === false ? this.cfg.checkoutUrl : this.cfg.cartUrl;
      }));

      // stay in sync with the cart: mini cart, free tote, back button, other tabs
      const sync = () => this.refreshCart();
      document.addEventListener('il:cart-changed', (e) => { if (!e.detail || e.detail.source !== 'il-family-builder') sync(); });
      document.addEventListener('cart:update', sync);
      window.addEventListener('pageshow', (e) => { if (e.persisted) sync(); });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') sync(); });
    }

    buildMeterSteps() {
      const ol = this.querySelector('[data-il-meter-steps]');
      if (!this.tiers.length) { this.querySelector('[data-il-meter]').hidden = true; return; }
      const short = (c) => formatMoney(c, this.cfg.moneyFormat).replace(/[.,]00(?!\d)/, '');
      const S = this.steps;
      ol.style.setProperty('--cols', S.length);
      ol.innerHTML = S.map((t, i) => t.start ? `
        <li data-qty="${t.qty}" class="is-start">
          <span class="il-fb-meter__dot">${t.qty}</span>
          <span class="il-fb-meter__save">${escapeHtml(this.t.start || '')}</span>
        </li>` : `
        <li data-qty="${t.qty}">
          <span class="il-fb-meter__dot">${t.qty}${i === S.length - 1 ? '+' : ''}</span>
          <span class="il-fb-meter__save">−${escapeHtml(short(t.amount))}</span>
          ${t.ship ? '<span class="il-fb-meter__ship">' + escapeHtml(this.t.free) + '</span>' : ''}
        </li>`).join('');
    }

    /* fill position: each tier sits at the centre of its column */
    meterPercent(n) {
      const T = this.steps.map((t) => t.qty);
      const col = 100 / T.length;
      if (n <= 0) return 0;
      if (n < T[0]) return (n / T[0]) * (col / 2);
      for (let i = 0; i < T.length - 1; i++) {
        if (n < T[i + 1]) return col * (i + 0.5) + ((n - T[i]) / (T[i + 1] - T[i])) * col;
      }
      return 100;
    }

    render(bump) {
      const money = (c) => formatMoney(c, this.cfg.moneyFormat);
      const short = (c) => money(c).replace(/[.,]00(?!\d)/, '');
      const lines = this.lines();
      const n = this.count();
      const { cur, next } = this.tierFor(n);
      // extra saving unlocked by the NEXT piece (only when one piece is missing)
      const stepSaving = next && next.qty - n === 1 ? next.amount - (cur ? cur.amount : 0) : 0;
      // "+ free delivery" only if the next tier unlocks it (not already unlocked)
      const shipTail = !!(next && next.ship && !(cur && cur.ship));

      // real cart totals (automatic discounts are already in the cart)
      const original = lines.reduce((s, x) => s + (x.original_line_price || x.line_price || 0), 0);
      const total = this.cart ? this.cart.total_price : 0;
      const realSaving = Math.max(0, original - total);
      // before Shopify has applied the discount, show the tier amount as an estimate
      const saving = n ? (realSaving || (cur ? cur.amount : 0)) : 0;
      const estTotal = realSaving ? total : Math.max(0, original - saving);

      // list = every line of the cart
      const list = this.querySelector('[data-il-list]');
      list.innerHTML = lines.map((x) => {
        const gift = this.isGift(x);
        const img = x.image ? x.image + (x.image.indexOf('?') > -1 ? '&' : '?') + 'width=160' : '';
        const variant = x.variant_title && x.variant_title !== 'Default Title' ? x.variant_title : '';
        const isNew = this._newId && x.variant_id === this._newId;
        return `
        <li class="il-fb-line${gift ? ' il-fb-line--gift' : ''}${isNew ? ' il-fb-line--new' : ''}">
          ${img ? `<img src="${escapeHtml(img)}" alt="" width="64" height="64" loading="lazy">` : '<span class="il-fb-line__ph"></span>'}
          <div class="il-fb-line__info">
            <strong>${escapeHtml(x.product_title)}</strong>
            ${variant ? `<small>${escapeHtml(variant)}</small>` : ''}
            <span>${x.original_line_price > x.final_line_price ? `<s>${escapeHtml(money(x.original_line_price))}</s> ` : ''}${escapeHtml(money(x.final_line_price))}${gift ? ` <em class="il-fb-line__gift">${escapeHtml(this.t.gift)}</em>` : ''}</span>
          </div>
          ${gift ? '<div class="il-fb-line__qty il-fb-line__qty--gift"><b>' + x.quantity + '</b></div>' : `
          <div class="il-fb-line__qty">
            <button type="button" data-il-qty="-1" data-key="${escapeHtml(x.key)}" aria-label="${escapeHtml(this.t.remove)}">${x.quantity > 1 ? '−' : '×'}</button>
            <b>${x.quantity}</b>
            <button type="button" data-il-qty="1" data-key="${escapeHtml(x.key)}" aria-label="+1">+</button>
          </div>`}
        </li>`;
      }).join('');
      this._newId = null;
      this.querySelector('[data-il-empty]').hidden = n > 0;

      // counts
      const label = `${n} ${n === 1 ? this.t.piece : this.t.pieces}`;
      this.querySelectorAll('[data-il-count]').forEach((el) => { el.textContent = n; });
      this.querySelector('[data-il-count-label]').textContent = label;

      // meter
      if (this.tiers.length) {
        const pct = this.meterPercent(n) + '%';
        this.querySelector('[data-il-meter-fill]').style.width = pct;
        this.querySelectorAll('[data-il-meter-steps] li').forEach((li) => {
          li.classList.toggle('is-on', n >= Number(li.dataset.qty));
          li.classList.toggle('is-next', !!next && Number(li.dataset.qty) === next.qty);
        });
        let msg = '';
        if (next) {
          msg = this.t.next.replace('[n]', next.qty - n).replace('[amount]', short(next.amount)) + (shipTail ? ' + ' + this.t.free : '');
          if (cur) msg = this.t.unlocked.replace('[amount]', short(cur.amount)) + ' · ' + msg;
        } else if (cur) {
          msg = this.t.best.replace('[amount]', short(cur.amount)) + (cur.ship ? ' + ' + this.t.free : '');
        }
        // "Your 4th piece for only [149 QAR] + free delivery"
        let msgHtml = escapeHtml(msg);
        let minReal = -1;
        if (stepSaving > 0) {
          this.querySelectorAll('[data-il-card]').forEach((card) => {
            if (!card._data || !card._data.variants.some((v) => v.a)) return;
            const real = Math.max(0, card._minPrice - stepSaving);
            if (minReal < 0 || real < minReal) minReal = real;
          });
        }
        if (minReal >= 0) {
          const ord = ordinal(n + 1, this.cfg.lang);
          const tail = shipTail ? ' + ' + this.t.free : '';
          // a piece that becomes free (saving step ≥ its price) -> "Your 4th piece is FREE"
          const isFree = minReal <= 0 && this.t.onlyFree;
          const priceTxt = isFree ? this.t.freeWord : short(minReal);
          const parts = (isFree ? this.t.onlyFree : this.t.only).replace('[ord]', ord).split('[price]');
          msg = parts[0] + priceTxt + (parts[1] || '') + tail;
          msgHtml = escapeHtml(parts[0]) + '<span class="il-cpack__pill">' + escapeHtml(priceTxt) + '</span>' + escapeHtml(parts[1] || '') + escapeHtml(tail);
        }
        const msgEl = this.querySelector('[data-il-msg]');
        msgEl.innerHTML = msgHtml;
        msgEl.classList.toggle('is-win', !!cur);
        this.querySelector('[data-il-bar-msg]').textContent = n ? msg : this.t.empty;
        const cartNote = this.querySelector('[data-il-cartnote]');
        if (cartNote) cartNote.hidden = true;

        // mobile bar: progress + "3 pieces · Your 4th piece for only [149]"
        const mFill = this.querySelector('[data-il-mbar-fill]');
        if (mFill) mFill.style.width = pct;
        const mMsg = this.querySelector('[data-il-mbar-msg]');
        if (mMsg) mMsg.innerHTML = n ? escapeHtml(label) + ' · ' + msgHtml : (msgHtml || escapeHtml(this.t.empty));
      }

      // totals — nothing is shown while the pack (cart) is empty
      this.querySelector('[data-il-subtotal]').textContent = n ? money(original) : '—';
      this.querySelector('[data-il-saving]').textContent = saving ? '−' + money(saving) : '—';
      this.querySelector('[data-il-saving]').closest('div').classList.toggle('is-zero', !saving);
      this.querySelector('[data-il-ship]').hidden = !(n && cur && cur.ship);
      this.querySelector('[data-il-total]').textContent = n ? money(estTotal) : '—';
      const savedEl = this.querySelector('[data-il-saved]');
      if (savedEl) {
        savedEl.hidden = !saving;
        savedEl.querySelector('[data-il-saved-text]').textContent = saving ? this.t.saved.replace('[amount]', short(saving)) : '';
      }
      // mobile bar: total + "✓ You saved QAR 60"
      const mTotal = this.querySelector('[data-il-mbar-total]');
      if (mTotal) mTotal.textContent = n ? short(estTotal) : '—';
      const mSaved = this.querySelector('[data-il-mbar-saved]');
      if (mSaved) {
        mSaved.hidden = !saving;
        mSaved.querySelector('[data-il-mbar-saved-text]').textContent = saving ? this.t.saved.replace('[amount]', short(saving)) : '';
      }
      this.querySelectorAll('[data-il-checkout]').forEach((btn) => { btn.disabled = n === 0; });
      this.tray.classList.toggle('has-items', n > 0);

      // per-card counters + "🎁 Your 4th piece: QAR 149" (real price stays on the card)
      this.querySelectorAll('[data-il-card]').forEach((card) => {
        const ids = card._data ? card._data.variants.map((v) => v.id) : [];
        const c = lines.filter((x) => ids.includes(x.variant_id)).reduce((s, x) => s + x.quantity, 0);
        const badge = card.querySelector('[data-il-card-count]');
        badge.hidden = !c;
        badge.textContent = '×' + c;
        card.classList.toggle('is-in-pack', c > 0);

        if (card._dealEl) {
          const show = stepSaving > 0 && card._data && card._data.variants.some((v) => v.a);
          card._dealEl.hidden = !show;
          if (show) {
            const real = Math.max(0, card._minPrice - stepSaving);
            const priceTxt = real <= 0 && this.t.freeWord ? this.t.freeWord : short(real);
            card._dealEl.querySelector('[data-il-deal-text]').textContent =
              this.t.deal.replace('[ord]', ordinal(n + 1, this.cfg.lang)).replace('[price]', priceTxt);
          }
        }
      });

      if (bump) {
        this.tray.classList.remove('is-bump'); void this.tray.offsetWidth; this.tray.classList.add('is-bump');
      }
    }

    fly(card) {
      const img = card.querySelector('img');
      const target = window.matchMedia('(max-width: 989px)').matches
        ? this.querySelector('.il-fb-mbar__info')
        : this.querySelector('.il-fb-tray__title');
      if (!img || !target || !img.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const a = img.getBoundingClientRect();
      const b = target.getBoundingClientRect();
      const ghost = img.cloneNode();
      ghost.removeAttribute('srcset');
      ghost.className = 'il-fb-ghost';
      Object.assign(ghost.style, { left: a.left + 'px', top: a.top + 'px', width: a.width + 'px', height: a.height + 'px' });
      document.body.appendChild(ghost);
      const dx = b.left + b.width / 2 - (a.left + a.width / 2);
      const dy = b.top + b.height / 2 - (a.top + a.height / 2);
      ghost.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '6px' },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 60}px) scale(.45)`, opacity: 0.95, borderRadius: '40%' },
        { transform: `translate(${dx}px, ${dy}px) scale(.08)`, opacity: 0.2, borderRadius: '50%' }
      ], { duration: 750, easing: 'cubic-bezier(.6,.05,.3,1)' }).onfinish = () => ghost.remove();
    }
  }

  customElements.define('il-family-builder', ILFamilyBuilder);
})();
